import React, { useState, useEffect, useRef } from 'react';
import { Button, Popover, List, ListItem, Box } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import './filterGroupButtons.css';
import { formatLabel } from './config';

interface FilterGroupButtonsProps {
  filterCategories: Array<{ id: string; label: string }>;
  selectedCategory: string;
  onCategoryChange: (categoryId: string, categoryLabel: string) => void;
}

const createCategoryKey = (category: { id: string; label: string }): string => {
  return `${category.id}:${category.label}`;
};

const FilterGroupButtons: React.FC<FilterGroupButtonsProps> = ({
  filterCategories,
  selectedCategory,
  onCategoryChange,
}) => {
  const [visibleButtons, setVisibleButtons] = useState<
    Array<{ id: string; label: string }>
  >([]);
  const [moreOptions, setMoreOptions] = useState<
    Array<{ id: string; label: string }>
  >([]);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const prevSelectedRef = useRef<string | null>(null);
  const firstRenderRef = useRef(true);

  // Animation effect when selected category changes
  useEffect(() => {
    // Skip animation on first render
    if (firstRenderRef.current) {
      firstRenderRef.current = false;
      prevSelectedRef.current = selectedCategory;
      return;
    }

    // Trigger animation when selection changes
    if (prevSelectedRef.current !== selectedCategory) {
      setIsAnimating(true);

      const timer = setTimeout(() => {
        setIsAnimating(false);
      }, 800);

      prevSelectedRef.current = selectedCategory;
      return () => clearTimeout(timer);
    }
  }, [selectedCategory]);

  useEffect(() => {
    const visibleCats = filterCategories.slice(0, 6);
    const moreCats = filterCategories.slice(6);

    const selectedCategoryObj = filterCategories.find(
      (cat) => createCategoryKey(cat) === selectedCategory
    );

    if (!selectedCategoryObj) {
      setVisibleButtons(visibleCats);
      setMoreOptions(moreCats);
      return;
    }

    const selectedInMore = moreCats.findIndex(
      (cat) => createCategoryKey(cat) === selectedCategory
    );

    if (selectedInMore !== -1) {
      const selectedCat = moreCats.splice(selectedInMore, 1)[0];
      visibleCats.pop();
      visibleCats.unshift(selectedCat);
    } else {
      const visibleSelectedIndex = visibleCats.findIndex(
        (cat) => createCategoryKey(cat) === selectedCategory
      );

      if (visibleSelectedIndex > 0) {
        const selectedCat = visibleCats.splice(visibleSelectedIndex, 1)[0];
        visibleCats.unshift(selectedCat);
      }
    }

    setVisibleButtons(visibleCats);
    setMoreOptions(moreCats);
  }, [filterCategories, selectedCategory]);

  const handleCategoryClick = (category: { id: string; label: string }) => {
    const categoryKey = createCategoryKey(category);
    if (categoryKey !== selectedCategory) {
      onCategoryChange(category.id, category.label);
    }
  };

  const handleMoreClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMoreClose = () => {
    setAnchorEl(null);
  };

  const handleMoreOptionClick = (category: { id: string; label: string }) => {
    onCategoryChange(category.id, category.label);
    handleMoreClose();
  };

  const open = Boolean(anchorEl);

  return (
    <Box className="filterGroupContainer">
      <Box className="filterButtonsWrapper">
        {visibleButtons.map((category, index) => {
          const categoryKey = createCategoryKey(category);
          return (
            <div
              key={categoryKey}
              className={`buttonWrapper ${
                categoryKey === selectedCategory ? 'selectedButtonWrapper' : ''
              } ${
                categoryKey === selectedCategory && isAnimating && index === 0
                  ? 'firstSelected'
                  : ''
              }`}
            >
              <Button
                className={`filterGroupButton ${
                  categoryKey === selectedCategory ? 'selected' : ''
                }`}
                onClick={() => handleCategoryClick(category)}
                variant={
                  categoryKey === selectedCategory ? 'contained' : 'outlined'
                }
                disableElevation
              >
                {formatLabel(category.label)}
              </Button>
            </div>
          );
        })}

        {moreOptions.length > 0 && (
          <Button
            className="moreButton"
            endIcon={<KeyboardArrowDownIcon />}
            onClick={handleMoreClick}
            variant="outlined"
          >
            More
          </Button>
        )}

        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={handleMoreClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          classes={{
            paper: 'morePopover',
          }}
        >
          <List className="moreOptionsList">
            {moreOptions.map((category) => {
              const categoryKey = createCategoryKey(category);
              return (
                <ListItem
                  button
                  key={categoryKey}
                  onClick={() => handleMoreOptionClick(category)}
                  className={
                    categoryKey === selectedCategory ? 'selectedOption' : ''
                  }
                >
                  {formatLabel(category.label)}
                </ListItem>
              );
            })}
          </List>
        </Popover>
      </Box>
    </Box>
  );
};

export default FilterGroupButtons;
